import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { AppSidebar } from "./AppSidebar";
import { Header } from "./Header";
import { useTraccarSession } from "@/hooks/useTraccarSession";
import { FleetAssistantSidebar } from "@/components/chat/FleetAssistantSidebar";

interface DashboardLayoutProps {
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  headerActions?: React.ReactNode;
  transparentHeader?: boolean;
  sidebar?: React.ReactNode;
}

export function DashboardLayout({ children, headerContent, headerActions, transparentHeader = false, sidebar }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const { user } = useTraccarSession();

  // Fleet Assistant state
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || 'U';

  const handleLogout = async () => {
    await fetch('/api/session', {method: 'delete'})
    navigate('/login');
  };

  const handleShowHtml = (invocation: { params: { html: string } }) => {
    if (invocation?.params?.html) {
      setHtmlContent(invocation.params.html);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="min-w-0 h-svh overflow-hidden flex flex-row">
        {sidebar}
        <div className={`flex flex-col flex-1 min-w-0 ${transparentHeader ? 'relative' : ''}`}>
          <Header
            userInitials={userInitials}
            userEmail={user?.email}
            onLogout={handleLogout}
            headerActions={headerActions}
            onAssistantClick={() => setIsAssistantOpen(!isAssistantOpen)}
            isAssistantOpen={isAssistantOpen}
            transparent={transparentHeader}
          >
            {headerContent}
          </Header>
          <ResizablePanelGroup direction="horizontal" className={transparentHeader ? 'h-full' : 'flex-1 min-h-0'}>
            <ResizablePanel defaultSize={isAssistantOpen ? 75 : 100} minSize={50}>
              <main className="h-full overflow-y-auto overflow-x-hidden relative">
                {htmlContent && isAssistantOpen ? (
                  <div className="absolute inset-0 bg-white z-10">
                    <iframe
                      srcDoc={htmlContent}
                      className="w-full h-full border-0"
                      title="Generated Content"
                    />
                  </div>
                ) : (
                  children
                )}
              </main>
            </ResizablePanel>
            {isAssistantOpen && (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={25} minSize={15} maxSize={50}>
                  <FleetAssistantSidebar
                    isOpen={isAssistantOpen}
                    onClose={() => setIsAssistantOpen(false)}
                    onShowHtml={handleShowHtml}
                  />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
